import React, { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import { addTacheCandidat, deleteTacheCandidat, editTacheCandidat, getAllMesTaches } from '../../../services/TacheCandidatService';
import FormTacheModal from '../../../components/FormTacheModal/FormTacheModal';
import ViewTacheModal from '../../../components/ViewTacheModal/ViewTacheModal';
import LabelHtmlToTextCustom from '../../../components/LabelHtmlToTextCustom/LabelHtmlToTextCustom';
import DialogDeleteCustom from '../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { commonGridSx, gridContainerSx, gridLocaleText, makeGridToolbar } from '../../../utils/gridStyles';
import { DataGrid } from '../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Tâches Candidats');

const statusConfig = {
  'à faire': { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  'en cours': { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' },
  'terminée': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'fait': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'annulée': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

function CandidatTasksTable({ snackbarShowMessage, searchParams, reloadingData, setReloadingData }) {
  const [loading, setLoading] = useState(false);
  const [tachesCandidat, setTachesCandidat] = useState([]);
  const [tacheCandidatInfo, setTacheCandidatInfo] = useState({ affectePar: '', tacheId: '' });
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);

  const props = { reloadingData, setReloadingData, editTache: editTacheCandidat, addTache: addTacheCandidat };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setTachesCandidat([]);
      try {
        const res = await getAllMesTaches(qs.stringify(searchParams));
        if (!active) return;
        setTachesCandidat(res.data.TachesCandidat || []);
      } catch {
        snackbarShowMessage('Erreur de chargement des tâches candidat.', 'error', 4000);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadingData]);

  const handleOpenDialog = (row) => {
    setTacheCandidatInfo({ affectePar: row.affectePar, tacheId: row.id });
    setOpenDialogDelete(true);
  };

  const handleCloseDialog = () => { if (!loadingToDelete) setOpenDialogDelete(false); };

  const handleDeleteTask = async () => {
    setLoadingToDelete(true);
    try {
      snackbarShowMessage('Patientez svp!', 'warning', null);
      await deleteTacheCandidat(tacheCandidatInfo.tacheId);
      snackbarShowMessage('Tâche de candidat bien supprimée!', 'success', 4000);
      handleCloseDialog();
      setReloadingData(!reloadingData);
    } catch {
      snackbarShowMessage('Erreur de suppression de la données, veuillez réssayer plutard.', 'error', 4000);
    } finally {
      setLoadingToDelete(false);
    }
  };

  const columns = [
    {
      field: 'affecteANom', headerName: 'AFFECTÉE À', flex: 150, minWidth: 150,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#334155' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'statut', headerName: 'STATUT', flex: 120, minWidth: 120,
      renderCell: (p) => {
        // The API sends the status as a boolean in `status`; `statut` is only set by older endpoints.
        const label = p.value || (p.row.status === true ? 'Fait' : p.row.status === false ? 'À faire' : '');
        const key = label.toLowerCase();
        const cfg = statusConfig[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return (
          <Chip label={label || '—'} size="small"
            sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '1rem', height: 22 }} />
        );
      },
    },
    {
      field: 'createdAt', headerName: 'CRÉÉ LE', flex: 120, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'dateTime', headerName: 'À FAIRE LE', flex: 120, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value ? moment(p.value).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'candidat', headerName: 'CANDIDAT', flex: 160, minWidth: 160,
      renderCell: (p) => p.value ? (
        <Link to={`/candidats/${p.row.condidatId}/view?tab=taches`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem' }}>
          {p.value}
        </Link>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'fonction', headerName: 'FONCTION', flex: 200, minWidth: 200,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'societe', headerName: 'SOCIÉTÉ', flex: 160, minWidth: 160,
      renderCell: (p) => p.value ? (
        <Link to={`/societe/${p.row.societeUuid}/view`}
          style={{ textDecoration: 'none', color: '#0ea5e9', fontSize: '1rem' }}>
          {p.value}
        </Link>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'affectePar', headerName: 'AFFECTÉE PAR', flex: 150, minWidth: 150,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'commentaire', headerName: 'NOTE', flex: 220, minWidth: 220,
      renderCell: (p) => p.value ? (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 210 }}>
          <LabelHtmlToTextCustom data={p.value} />
        </Box>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '1rem' }}>—</Typography>,
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <ViewTacheModal
            affecteA={p.row.affecteANom}
            affectePar={p.row.affectePar}
            commentaire={(p.row.commentaire || '').replace(/<[^>]*>?/gm, '')}
            statut={p.row.statut || p.row.status}
            dateTime={p.row.dateTime?.date || p.row.dateTime}
            createdAt={p.row.createdAt}
            candidat={p.row.candidat}
            candidatLink={p.row.condidatId ? `/candidats/${p.row.condidatId}/view?tab=taches` : undefined}
            fonction={p.row.fonction}
            societe={p.row.societe}
            societeLink={p.row.societeUuid ? `/societe/${p.row.societeUuid}/view` : undefined}
          />
          <FormTacheModal
            {...{ ...props, id: p.row.uuid }}
            editMode={true}
            oldAffecteA={p.row.affecteA}
            oldStatut={p.row.status}
            oldCommentaire={p.row.commentaire}
            oldDateTime={p.row.dateTime?.date || p.row.dateTime}
            tacheId={p.row.id}
          />
          <Tooltip title="Supprimer">
            <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
              sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = (tachesCandidat || []).map((row) => ({
    ...row,
    id: row.id,
    createdAt: row.createdAt?.date ? moment(row.createdAt.date).toDate().getTime() : row.createdAt,
    dateTime: row.dateTime?.date ? moment(row.dateTime.date).toDate().getTime() : row.dateTime,
  }));

  return (
    <>
      <Box sx={{ ...gridContainerSx, height: 'calc(100vh - 240px)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
      <DialogDeleteCustom
        handleClose={handleCloseDialog}
        open={openDialogDelete}
        handleConfirm={handleDeleteTask}
        loading={loadingToDelete}
        label={`Voulez vous vraiment supprimer la tâche de ${tacheCandidatInfo.affectePar}?`}
        title="Supprimer la tâche"
      />
    </>
  );
}

export default withSnackbar(CandidatTasksTable);
